"use client";

import Image from "next/image";
import { Link, usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "./auth-context";

export function UserMenu() {
  const { user, isLoading, isAdmin, avatarUrl: profileAvatarUrl } = useAuth();
  const pathname = usePathname();
  const t = useTranslations("Common");

  if (isLoading || !user) {
    const loginHref =
      pathname && pathname !== "/"
        ? `/auth/login?redirect=${encodeURIComponent(pathname)}`
        : "/auth/login";

    return (
      <Link
        href={loginHref}
        rel="nofollow"
        prefetch={false}
        className="font-mono text-xs text-neutral-400 transition-colors hover:text-acid"
      >
        {t("signIn")}
      </Link>
    );
  }

  const avatarUrl = profileAvatarUrl || (user.user_metadata?.avatar_url as string | undefined);
  const initial = (
    user.user_metadata?.display_name?.[0] ||
    user.email?.[0] ||
    "?"
  ).toUpperCase();

  return (
    <div className="flex items-center gap-3">
      {isAdmin && (
        <Link
          href="/admin"
          rel="nofollow"
          prefetch={false}
          className="text-neutral-400 transition-colors hover:text-acid"
          aria-label={t("admin")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Link>
      )}
      <Link
        href="/profile"
        rel="nofollow"
        prefetch={false}
        className="flex items-center transition-colors hover:text-acid"
        aria-label={t("profile")}
      >
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 rounded-full object-cover"
            unoptimized
          />
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-acid/20 text-[10px] font-bold text-acid">
            {initial}
          </div>
        )}
      </Link>
    </div>
  );
}
